import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon, XCircleIcon } from 'lucide-react';
export function WhyDiagnosticMatters() {
  const basicApproach = ['Read a fault code', 'Replace the most likely part', 'See if it goes away', 'No inspection report'];
  const lancerApproach = ['Advanced system scanning and live data analysis', 'Manufacturer diagnostic procedures followed', 'Root cause confirmed before any repair is recommended', '42-point Digital Vehicle Inspection with photos and video sent to your phone'];
  return <section id="what-we-do" className="py-20 px-4 bg-white pt-[50px] pb-[50px]" data-id="element-219">
      <div className="max-w-5xl mx-auto" data-id="element-220">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-221">
          <span className="uppercase tracking-widest text-sm font-bold text-brand-accent mb-4 block" data-id="element-222">
            Why your shop's tools matter
          </span>
          <h2 className="font-heading uppercase tracking-wide text-3xl md:text-4xl lg:text-5xl font-extrabold text-brand-dark mb-12" data-id="element-223">
            Modern cars run on software as much as parts.
          </h2>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="space-y-6 text-lg md:text-xl text-gray-700 leading-relaxed text-center max-w-4xl mx-auto mb-16" data-id="element-224">
          <p className="pl-[0px] pr-[0px]" data-id="element-225">
            Vehicles built in the last ten years contain dozens of
            interconnected computer systems — engine management, transmission
            control, safety sensors, driver-assist features, emissions
            monitoring. Diagnosing them correctly requires current equipment,
            updated software, and technicians trained on how those systems
            actually work.
          </p>

          <p data-id="element-226">
            A shop running older tools can still change your oil and replace
            your brakes. What they may not be able to do is accurately diagnose
            an electrical fault, service your vehicle's safety systems, or catch
            a developing problem in a system they can't fully read.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 mb-16" data-id="element-227">
          {/* Basic Approach Column */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-brand-light border border-gray-200 rounded-2xl p-8 md:p-10 flex flex-col shadow-sm" data-id="element-228">
            <h3 className="font-heading uppercase tracking-wide text-xl font-bold text-gray-500 mb-8 pb-4 border-b border-gray-200" data-id="element-229">
              Basic shop approach
            </h3>
            <ul className="space-y-6 flex-grow" data-id="element-230">
              {basicApproach.map((item, index) => <li key={index} className="flex items-start gap-4" data-id="element-231">
                  <XCircleIcon className="w-6 h-6 text-gray-400 flex-shrink-0 mt-0.5" data-id="element-232" />
                  <span className="text-gray-600 font-medium text-lg" data-id="element-233">
                    {item}
                  </span>
                </li>)}
            </ul>
          </motion.div>

          {/* Lancer's Approach Column */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="bg-white border-2 border-brand-accent rounded-2xl p-8 md:p-10 relative shadow-xl shadow-brand-accent/5" data-id="element-234">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-brand-accent text-white font-bold px-4 py-1 rounded-full text-sm" data-id="element-235">
              Lancer's approach
            </div>
            <h3 className="font-heading uppercase tracking-wide text-xl font-bold text-brand-dark mb-8 pb-4 border-b border-gray-100" data-id="element-236">
              Comprehensive Diagnostics
            </h3>
            <ul className="space-y-6" data-id="element-237">
              {lancerApproach.map((item, index) => <li key={index} className="flex items-start gap-4" data-id="element-238">
                  <CheckCircleIcon className="w-6 h-6 text-brand-accent flex-shrink-0 mt-0.5" data-id="element-239" />
                  <span className="text-brand-dark font-medium text-lg" data-id="element-240">
                    {item}
                  </span>
                </li>)}
            </ul>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-241">
          <p className="text-xl md:text-2xl text-brand-dark font-bold" data-id="element-242">
            The repair that didn't fix it usually wasn't a bad part. It was an
            incomplete diagnosis.
          </p>
        </motion.div>
      </div>
    </section>;
}